import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Box,
} from "@material-ui/core";

import BootstrapTable from "react-bootstrap-table-next";
import "../style.css";
import { useSelector, useDispatch } from "react-redux";
import {
  createEmailConfigurations,
  updateEmailConfigurations,
  getEmailConfigurations,
  getEmailMasterData,
} from "./action";

export default function EmailPopup({ open, onClose, isEdit, selectedRow }) {
  const dispatch = useDispatch();
  const [selectedGSTIN, setSelectedGSTIN] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [existingEmails, setExistingEmails] = useState([]);
  const gstins = useSelector((state) => state.noticeReducer.gstinList);
  const selectedGstin = useSelector(
    (state) => state.noticeReducer.selectedGstin
  );

  // Function to fetch emails for selected GSTIN
  const fetchEmailsForGstin = async (gstin) => {
    try {
      const result = await dispatch(getEmailMasterData(gstin));
      if (result.success && result.data && result.data.length > 0) {
        // Extract emails from the API response structure
        const emails = result.data.flatMap((item) =>
          item.dataJson ? item.dataJson.map((emailObj) => emailObj.Email) : []
        );
        setExistingEmails(emails);
        console.log("Existing emails for GSTIN:", emails);
      } else {
        setExistingEmails([]);
      }
    } catch (error) {
      console.log("Error fetching emails for GSTIN:", error);
      setExistingEmails([]);
    }
  };

  // Function to remove email from table
  const handleRemoveEmail = (gstin, emailToRemove) => {
    setRows((prevRows) => {
      return prevRows
        .map((row) => {
          if (row.gstin === gstin) {
            const updatedEmails = row.emails.filter(
              (email) => email !== emailToRemove
            );
            // If no emails left, remove the entire row
            if (updatedEmails.length === 0) {
              return null;
            }
            return { ...row, emails: updatedEmails };
          }
          return row;
        })
        .filter(Boolean); // Remove null values
    });
  };

  const [rows, setRows] = useState([]);

  // useEffect to handle edit mode - prefill form with selectedRow data
  useEffect(() => {
    if (isEdit && selectedRow && selectedRow.gstin) {
      // Set the selected GSTIN
      setSelectedGSTIN(selectedRow.gstin);

      // Transform selectedRow into rows format
      const editRowData = {
        gstin: selectedRow.gstin,
        emails: selectedRow.email || [],
      };
      setRows([editRowData]);

      // Fetch available emails for the selected GSTIN
      fetchEmailsForGstin(selectedRow.gstin);
    } else {
      // Reset form for add mode
      setRows([]);
      setSelectedGSTIN("");
      setSelectedEmail("");
      setExistingEmails([]);
    }
  }, [isEdit, selectedRow, open]);

  // Create available GSTIN options - in edit mode show only selectedRow.gstin
  const availableGstins = React.useMemo(() => {
    if (isEdit && selectedRow && selectedRow.gstin) {
      // In edit mode, only show the GSTIN from selectedRow
      return [selectedRow.gstin];
    }
    return gstins || [];
  }, [gstins, isEdit, selectedRow]);

  const handleAddIteration = () => {
    if (!selectedGSTIN || !selectedEmail) return;

    setRows((prevRows) => {
      const existingRow = prevRows.find((r) => r.gstin === selectedGSTIN);

      // If row exists → modify it
      if (existingRow) {
        // Duplicate email check
        if (existingRow.emails.includes(selectedEmail)) {
          alert("This email already exists for this GSTIN");
          return prevRows;
        }

        // Max 5 emails check (including existing emails from API)
        if (existingRow.emails.length >= 5) {
          alert("A GSTIN can have only 5 emails");
          return prevRows;
        }

        // Add email to existing row
        return prevRows.map((r) =>
          r.gstin === selectedGSTIN
            ? { ...r, emails: [...r.emails, selectedEmail] }
            : r
        );
      }

      return [...prevRows, { gstin: selectedGSTIN, emails: [selectedEmail] }];
    });

    // Clear email selection
    setSelectedEmail("");
  };

  const columns = [
    {
      dataField: "gstin",
      text: "GSTIN",
      headerStyle: {
        background: "#4c2380",
        color: "white",
        width: "25%",
        borderRight: "none",
      },
      style: {
        fontWeight: 600,
        width: "25%",
        textAlign: "center",
        verticalAlign: "middle",
        borderRight: "none",
      },
    },
    {
      dataField: "emails",
      text: "Email",
      headerStyle: { background: "#4c2380", color: "white", width: "75%" },
      style: { width: "75%", verticalAlign: "middle" },
      formatter: (cell, row) => (
        <div>
          {cell.map((email, index) => (
            <Box
              key={index}
              style={{
                padding: "6px 14px",
                marginLeft: "1em",
                marginBottom: "4px",
                display: "inline-block",
                borderRadius: 20,
                border: "2px solid rgb(79, 45, 127)",
                fontSize: 13,
                position: "relative",
                paddingRight: "30px", // Make space for the cross icon
              }}
            >
              {email}
              <span
                onClick={() => handleRemoveEmail(row.gstin, email)}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: "rgb(79, 45, 127)",
                  fontWeight: "bold",
                  fontSize: "14px",
                  width: "16px",
                  height: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  backgroundColor: "rgba(79, 45, 127, 0.1)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "rgba(79, 45, 127, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "rgba(79, 45, 127, 0.1)";
                }}
              >
                ×
              </span>
            </Box>
          ))}
        </div>
      ),
    },
  ];

  const handleSubmit = async () => {
    if (rows.length === 0) {
      alert("Please add at least one email configuration before submitting.");
      return;
    }

    try {
      // Transform rows to required API format
      const configData = rows.map((row) => ({
        gstin: row.gstin,
        email: row.emails,
      }));

      console.log("SUBMIT DATA:", configData);

      let result;
      if (isEdit) {
        // Use PUT API for editing
        result = await dispatch(updateEmailConfigurations(configData));
      } else {
        // Use POST API for creating new configurations
        result = await dispatch(createEmailConfigurations(configData));
      }

      if (result.success) {
        // Clear the rows after successful submission
        setRows([]);
        setSelectedGSTIN("");
        setSelectedEmail("");
        setExistingEmails([]);

        // Close the popup
        onClose();
        // Refresh the email configurations table
        await dispatch(getEmailConfigurations(selectedGstin));
      }
    } catch (error) {
      console.log("Error in handleSubmit:", error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ textAlign: "center", fontWeight: 600 }}>
        {isEdit ? "Edit Email Configuration" : "Add New Email Configuration"}
      </DialogTitle>

      <DialogContent>
        <Box
          style={{
            padding: 20,
            background: "#f5edff",
            borderRadius: 12,
            marginBottom: 20,
          }}
        >
          <Grid container spacing={2}>
            {/* GSTIN Dropdown */}
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Select GSTIN"
                variant="outlined"
                fullWidth
                size="small"
                value={selectedGSTIN}
                onChange={(e) => {
                  const newGstin = e.target.value;
                  setSelectedGSTIN(newGstin);
                  setSelectedEmail("");
                  if (newGstin) {
                    fetchEmailsForGstin(newGstin);
                  } else {
                    setExistingEmails([]);
                  }
                }}
                disabled={isEdit} // Disable GSTIN selection in edit mode
                style={{ backgroundColor: "white" }}
                SelectProps={{
                  MenuProps: {
                    getContentAnchorEl: null, // IMPORTANT
                    anchorOrigin: {
                      vertical: "bottom", // place menu under the field
                      horizontal: "left",
                    },
                    //   transformOrigin: {
                    //     vertical: -20,          // shift menu down or up
                    //     horizontal: 100         // shift left or right
                    //   }
                  },
                }}
              >
                {availableGstins.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Email Dropdown */}
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Select Email"
                variant="outlined"
                fullWidth
                size="small"
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
                disabled={!selectedGSTIN}
                style={{ backgroundColor: "white" }}
                SelectProps={{
                  MenuProps: {
                    getContentAnchorEl: null, // IMPORTANT
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                  },
                }}
              >
                {existingEmails.map((e) => {
                  // Check if this email is already added in the current session for this GSTIN
                  const currentRow = rows.find(
                    (r) => r.gstin === selectedGSTIN
                  );
                  const isAlreadyAdded =
                    currentRow?.emails.includes(e) || false;

                  return (
                    <MenuItem
                      key={e}
                      value={e}
                      disabled={isAlreadyAdded}
                      style={{
                        opacity: isAlreadyAdded ? 0.5 : 1,
                        color: isAlreadyAdded ? "#999" : "inherit",
                      }}
                    >
                      {e}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>

            {/* Add Iteration */}
            <Grid
              item
              xs={12}
              md={2}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddIteration}
                style={{
                  background: "rgb(79, 45, 127)",
                  color: "white",
                  //   height: 48,
                  width: "100%",
                  borderRadius: 8,
                  textTransform: "capitalize",
                }}
              >
                + Add Iteration
              </Button>
            </Grid>
            <Grid item xs={12} md={4}></Grid>
          </Grid>
        </Box>

        {/* Table */}
        <div className="notice-email-addIteration" id="tt">
          <BootstrapTable
            keyField="gstin"
            data={rows}
            columns={columns}
            // bordered={false}
          />
        </div>
      </DialogContent>

      <DialogActions style={{ padding: "20px" }}>
        <Button
          variant="contained"
          color="primary"
          style={{
            background: "rgb(79, 45, 127)",
            color: "#fff",
            borderRadius: 8,
            textTransform: "capitalize",
          }}
          onClick={handleSubmit}
        >
          Submit
        </Button>

        <Button
          variant="outlined"
          onClick={onClose}
          style={{
            borderColor: "rgb(79, 45, 127)",
            color: "rgb(79, 45, 127)",
            borderRadius: 8,
            textTransform: "capitalize",
            marginRight: "8px",
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
